import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { ozonApi, needsSubscription } from '../../utils/ozon-api';
import { n8nFetch } from '../../utils/n8n-fetch';
import { WEBHOOK_ENDPOINTS } from '../../config/webhook-urls';

// Диспетчер «Ozon Comm API (multi)» — отзывы/вопросы из таблиц (кабинетный синк), ответы, счётчики.
export const ozonComm = async (action, userId, params = {}) => {
  const res = await n8nFetch(WEBHOOK_ENDPOINTS.OZON_COMM_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, user_id: userId, ...params }),
  });
  let data = null;
  try { data = await res.json(); } catch { /* пустое тело — сообщение соберём по статусу */ }
  if (Array.isArray(data)) data = (data[0] && (data[0].json || data[0])) || {};
  else if (data && data.json) data = data.json;
  if (!res.ok) throw new Error((data && data.error) || `Вебхук ozon-comm-api вернул HTTP ${res.status}`);
  if (data && data.ok === false) throw new Error(String(data.error || 'Ошибка ozon-comm-api'));
  return data || {};
};

// Необработанные отзывы и вопросы по всем фирмам области видимости — для бейджа в меню.
export const loadPendingCounts = async () => {
  const d = await ozonComm('counts', undefined);
  return { reviews: Number(d.reviews) || 0, questions: Number(d.questions) || 0 };
};

// Общее для /ozon-communication и её вкладок: passthrough, загрузчик, форматтеры.

// Кабинеты Ozon наших фирм — как на /ozon-orders: `ids` страхует сессию, залогиненную
// до миграции 121 (в её sellers_meta нет marketplaces); у клиента (isScoped) список свой.
// multi-ok: список наших ozon-кабинетов, у клиента не применяется.
export const OZON_FIRM_IDS = [1, 2];

export const errText = (d) => (typeof d === 'string' ? d : JSON.stringify(d || {})).slice(0, 300);

const pad = (n) => String(n).padStart(2, '0');
export const fmtDate = (s) => {
  const d = new Date(s || '');
  return Number.isNaN(d.getTime()) ? '' : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
export const fmtDateTime = (s) => {
  const d = new Date(s || '');
  return Number.isNaN(d.getTime()) ? '' : `${fmtDate(s)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const fmtMoney = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v))
  ? '—' : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 }));

// Общий вызов passthrough'а: не-2xx поднимаем ошибкой, 403 по подписке помечаем
// флагом wall — вкладка тогда рисует баннер вместо ошибки.
export const call = async (userId, path, body) => {
  const r = await ozonApi(userId, 'POST', path, body);
  if (!r.ok) {
    const e = new Error(`Ozon HTTP ${r.status}: ${errText(r.data)}`);
    e.wall = needsSubscription(r);
    throw e;
  }
  return r.data || {};
};

// Один загрузчик на вкладку: fn(firms) → массив строк, firms = [{user_id, label}]
// (одна выбранная фирма либо все при «Все»). Ссылки на fn и на firms должны быть
// стабильными (загрузчик — на уровне модуля, firms — useMemo), иначе эффект зациклится.
export function useOzon(firms, fn) {
  // loading сразу true при известной фирме — иначе первый кадр рисует «нет данных» до эффекта.
  const [state, setState] = useState({ rows: [], loading: !!firms.length, wall: false, error: '' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!firms.length) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: '' }));
    fn(firms).then(
      (rows) => { if (!cancelled) setState({ rows, loading: false, wall: false, error: '' }); },
      (e) => {
        if (cancelled) return;
        setState({ rows: [], loading: false, wall: !!e.wall, error: e.wall ? '' : String(e.message || e) });
      },
    );
    return () => { cancelled = true; };
  }, [firms, fn, tick]);
  return { ...state, reload: useCallback(() => setTick((t) => t + 1), []) };
}

// user_id для вебхука: одна фирма — её id, несколько — ключ не шлём вовсе, и бэк
// берёт область видимости (вся команда / свои фирмы у клиента).
export const scopeUid = (firms) => (firms.length === 1 ? firms[0].user_id : undefined);

// Кабинет Ozon один на фирму, общего эндпоинта нет — при «Все» ходим в каждый и склеиваем.
// Строки помечаем фирмой: в списке видно, чья строка, а действие уходит в её кабинет.
// Фирма без подписки (wall) или с упавшим кабинетом не гасит остальные; если легли все —
// поднимаем первую ошибку, как было с одной фирмой.
export const perFirm = async (firms, one) => {
  const settled = await Promise.all(firms.map((f) => one(f.user_id).then(
    (rows) => ({ rows: rows.map((r) => ({ ...r, user_id: f.user_id, firm_label: f.label })) }),
    (e) => ({ e }),
  )));
  const ok = settled.filter((s) => s.rows);
  if (!ok.length) throw settled[0].e;
  const out = ok.flatMap((s) => s.rows);
  // Упавшие фирмы — свойством на массиве (sort его сохраняет): вкладка покажет, чьих строк нет,
  // иначе протухший ключ второй фирмы выглядит как «у неё просто нет чатов».
  out.failed = firms.filter((f, i) => settled[i].e).map((f) => f.label);
  return out;
};

export const SubBanner = ({ name }) => (
  <Alert severity="warning" sx={{ fontSize: 12.5 }}>
    {`Нужна подписка Ozon: ${name}. Раздел включится сам, как только подписка появится в кабинете.`}
  </Alert>
);

export const Loader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
);

// ── Непрочитанное ───────────────────────────────────────────────────────────
// Ответ /v3/chat/list: [{chat:{…}, unread_count}] — раскладываем в плоскую строку.
export const normChats = (d) => (Array.isArray(d.chats) ? d.chats : [])
  .map((c) => ({ ...(c.chat || {}), unread_count: Number(c.unread_count) || 0 }));

// Бейдж считаем только по чатам с покупателями: остальные типы — служебные
// уведомления Ozon, они висят непрочитанными вечно и забили бы счётчик.
export const buyerUnread = (rows) => rows
  .filter((c) => c.chat_type === 'BUYER_SELLER')
  .reduce((s, c) => s + (Number(c.unread_count) || 0), 0);

// Один запрос на фирму — для бейджей в меню и на вкладке (без полного списка чатов).
export const loadBuyerUnread = async (userId) => buyerUnread(normChats(
  await call(userId, '/v3/chat/list', { filter: { chat_status: 'Opened', unread_only: true }, limit: 100 }),
));

// ── Заявки на возврат «На согласовании» ─────────────────────────────────────
// FBS-заявки, по которым Ozon ждёт решения продавца; таймер кабинета тикает — их видно
// сверху вкладки «Возвраты» и в бейдже меню. Seller API список отдаёт, решать не умеет.
export const loadPendingReturnRows = async (userId) => {
  const d = await call(userId, '/v1/returns/list', { limit: 100, filter: { visual_status_name: 'OnSellerApproval' } });
  return Array.isArray(d.returns) ? d.returns : [];
};

// Счётчик на фирму — для бейджей в меню и на вкладке.
export const loadPendingReturns = async (userId) => (await loadPendingReturnRows(userId)).length;
